/* AuditPolicyGridViewRenderer.js */

"use strict";

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");

class AuditPolicyExplorerViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("audit-policy-grid-view-gl");
        this._logger = this._model.logger;
        this._defaultViewActive = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "FeedInitialized",
            (this._onFeedInitialized = this._onFeedInitialized.bind(this))
        );
        // this._model.gameEditor.on('ColumnSettingsChange', this._onColumnSettingsChanged = this._onColumnSettingsChanged.bind(this));
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._policyEntityGrid.on(
            "UserSelectedRows",
            (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this))
        );
        this._policyEntityGrid.on(
            "UserRowDoubleClicked",
            (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
        );
        this._policyEntityGrid.on(
            "UserSorted",
            (this._onUserSorted = this._onUserSorted.bind(this))
        );
        this._policyEntityGrid.on(
            "GridBusyStart",
            (this._onGridBusyStart = this._onGridBusyStart.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._model) {
            this._model.removeListener("FeedInitialized", this._onFeedInitialized);
            this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        }
        if (this._policyEntityGrid) {
            this._unregisterGridListeners();
        }
    }

    _unregisterGridListeners() {
        if (this._policyEntityGrid) {
            this._policyEntityGrid.removeAllListeners("UserSelectedRows");
            this._policyEntityGrid.removeAllListeners("UserRowDoubleClicked");
            this._policyEntityGrid.removeListener("UserSorted", this._onUserSorted);
            this._policyEntityGrid.removeListener("GridBusyStart", this._onGridBusyStart);
        }
    }

    _renderAvailableActions() {
        let numSelected = this.selectedRows.length || 0;

        if (numSelected > 0) {
            this._model.gameEditor.enableAllActions();
        } else {
            this._model.gameEditor.disableAllActions();
            this._model.gameEditor.enableAction(Registrations.AUDIT_POLICY_EXPLORER_ACTION_CREATE);
            this._model.gameEditor.enableAction(Registrations.AUDIT_POLICY_EXPLORER_ACTION_IMPORT);
        }

        if (numSelected > 1) {
            this._model.gameEditor.disableAction(Registrations.AUDIT_POLICY_EXPLORER_ACTION_CLONE);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [
            _.isUndefined(this._policyEntityGrid)
                ? Promise.resolve()
                : this._policyEntityGrid.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._policyEntityGrid = undefined;
                this._defaultViewActive = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "PolicyGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(() => {
            this._policyEntityGrid = new EntityGrid({
                gridKey: this.model._gridKey,
                logger: this._logger,
                singleSelect: false,
                selectedRows: _.get(this._model.getSelectedPolicyIds(), "selectedRows", []),
                columns: this._model.columnConfig,
                pagingFeed: this._model.pagingFeed,
                showHeader: true,
                showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector(
                    "game-editor-view-actions-gl left-container"
                ),
                columnSettingsWidgetContainer:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                filterComponent:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                advancedFilterComponent:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                modalUserFeedback: this._model._modalUserFeedback,
                userGridData: this._model._userGridData,
            });

            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._policyEntityGrid.renderer.rootElement);
            }
            this._renderAvailableActions();
            this._onFeedRefreshView();
            this._registerListeners();

            if (
                this._model._gameEditor._openEditorFromUrl &&
                (this._model._gameEditor._openEditorFromUrl.open ||
                    !_.isUndefined(this._model._gameEditor._openEditorFromUrl.selectedIds))
            ) {
                _.get(this._model._gameEditor._openEditorFromUrl, "selectedIds", []).forEach(
                    (policyid) => {
                        let Policy = this._model.pagingFeed.getPolicyEntityById(policyid);
                        this._openEditor(Policy, policyid);
                    }
                );
            }

            if (this._model._gameEditor._filterFromUrl) {
                this._policyEntityGrid._advancedFilter._advancedFilter._renderer._applyQuery();
            }
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    get selectedRows() {
        return this._policyEntityGrid.getSelectedRows();
    }

    restartGrid() {
        //this._unregisterGridListeners();
        return this._policyEntityGrid.shutdown().then(() => {
            this._instantiateGrid();
            this._rootElem.appendChild(this._policyEntityGrid.renderer.rootElement);
            this._registerGridListeners();
            this._renderAvailableActions();
        });
    }

    /*******************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _showDefaultMessageDisplay() {
        let message;
        switch (this._model._solutionName) {
            case "config_drift":
                message = {
                    message: "Create a new policy",
                    icon: "configdrift.add_audit_policy",
                    action: Registrations.AUDIT_POLICY_EXPLORER_ACTION_CREATE,
                };
                break;
        }
        return this._model.gameEditor
            .showDefaultMessageView(message)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "PolicyListViewerRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "PolicyListViewerRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onGridBusyStart() {
        if (this._model.pagingFeed.totalEntities > this._model.pagingFeed.pageSize * 2) {
            let gridBusyEnded = false;
            this._policyEntityGrid.once("GridBusyEnd", () => {
                gridBusyEnded = true;
            });
            this._model._modalUserFeedback.displayLoading().then(() => {
                if (gridBusyEnded) {
                    this._model._modalUserFeedback.hideDisplayed();
                } else {
                    this._policyEntityGrid.once("GridBusyEnd", () => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                }
            });
        }
    }

    _onFeedInitialized() {
        // this._renderGridRows();
        this._renderAvailableActions();
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        let Policy = this._model.pagingFeed.getPolicyEntityById(obj.selectedRow);
        //const selectedPolicyIds = _.get(this._model.getSelectedPolicyIds(), "selectedRows", []);

        this._openEditor(Policy, obj.selectedRow);
    }

    _openEditor(Policy, policyId) {
        if (Policy.lastAuditBy) {
            //@toDo: rewrite to Policy.lastAuditStatus
            // Config Drift
            this._model.openPolicyInstanceEditor(
                policyId,
                Registrations.AUDIT_POLICY_EDITOR_VIEW_RESULTS
            );
        } else {
            // Default DIE view if neither in Config Drift nor in Policy Manager
            this._model.openPolicyInstanceEditor(
                policyId,
                Registrations.AUDIT_POLICY_EDITOR_VIEW_DETAILS
            );
        }
    }

    _onGridUserSelectedRows(selectedRowIds) {
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        this._renderAvailableActions();
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        const filterData = this._model.pagingFeed.getFilterData();

        if (totalEntities === 0 && _.isEmpty(filterData)) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    // TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = AuditPolicyExplorerViewRenderer;
